import type { SynastryV2Response } from '@/api/schema/synastry'
import { dayjs } from '@/api/utils/dayjs'
import { Emoji } from '@/app/components/Emoji/Emoji'
import { Col, Row } from '@/app/components/Flex/Flex'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import { useNavigate, useNavigateSearchParams } from '@/app/hooks/useNavigate'
import ChevronRightSvg from '@/app/icons/chevron-right.svg?react'
import { metrikaGoal } from '@/app/utils/metrika'
import type { FunctionalComponent } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'
import styles from './index.module.scss'

const QUESTION_TAB_PARAM = 'questionsTab'

function getPronouns(gender: SynastryV2Response['persone2Gender']) {
  return gender === 'female'
    ? {
      subject: 'она',
      dative: 'ей',
      genitive: 'неё',
      accusative: 'её',
    }
    : {
      subject: 'он',
      dative: 'ему',
      genitive: 'него',
      accusative: 'его',
    }
}

const TABS = [
  {
    id: 'love',
    icon: <Emoji type='hearts' />,
    title: 'Чувства',
    questions: (gender: SynastryV2Response['persone2Gender']) => {
      const pronouns = getPronouns(gender)

      return [
        `Есть ли у ${pronouns.genitive} чувства ко мне?`,
        `Чего ${pronouns.dative} не хватает во мне?`,
        `Почему ${pronouns.accusative} тянет ко мне?`,
        `Что во мне ${pronouns.accusative} зацепило?`,
        `Какую роль я играю в ${pronouns.accusative} жизни?`,
        `Что ${pronouns.subject} не решается сказать мне?`,
      ]
    },
  },
  {
    id: 'troubles',
    icon: <Emoji type='brokenLove' />,
    title: 'Сложности',
    questions: (gender: SynastryV2Response['persone2Gender']) => {
      const pronouns = getPronouns(gender)

      return [
        `Почему ${pronouns.subject} отдаляется от меня?`,
        'Что между нами идёт не так?',
        `Что ${pronouns.dative} сложно принять во мне?`,
        'Почему мы раним друг друга?',
        'Что мешает нам быть вместе?',
        `Чего ${pronouns.subject} боится в отношениях?`,
      ]
    },
  },
  {
    id: 'future',
    icon: <Emoji type='stars' />,
    title: 'Будущее',
    questions: (gender: SynastryV2Response['persone2Gender']) => {
      const pronouns = getPronouns(gender)

      return [
        'Что ждёт нас дальше?',
        `Когда ${pronouns.subject} сделает шаг?`,
        'Будет ли у нас серьёзно?',
        'Есть ли шанс быть вместе?',
        `Стоит ли ждать ${pronouns.accusative} дальше?`,
        `Выберет ли ${pronouns.subject} меня в итоге?`,
      ]
    },
  },
] as const

export const Questions: FunctionalComponent<{
  data: SynastryV2Response
}> = props => {
  const route = useNavigate()
  const [params, navigate] = useNavigateSearchParams()
  const swiperRef = useRef<SwiperType | null>(null)
  const tabParam = params.get(QUESTION_TAB_PARAM)
  const activeIdxFromParams = Math.max(0, TABS.findIndex(tab => tab.id === tabParam))

  function setTabParam(idx: number) {
    const nextTab = TABS[idx]?.id ?? TABS[0].id
    const currentTab = tabParam && TABS.some(tab => tab.id === tabParam)
      ? tabParam
      : TABS[0].id

    if (currentTab === nextTab) return

    navigate((prev) => {
      const next = new URLSearchParams(prev)
      next.set(QUESTION_TAB_PARAM, nextTab)

      return next
    }, {
      replace: true,
    })
  }

  function askQuestion(question: string) {
    metrikaGoal('synastry_question_click')

    const q = encodeURIComponent(
      `${props.data.persone2Name}, ${dayjs(props.data.persone2Date, 'YYYY-MM-DD').format('DD.MM.YYYY')}. ${question}`,
    )

    route(`~/chats/${props.data.type === 'astro' ? 'zoe' : 'milana'}?q=${q}`)
  }

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.activeIndex !== activeIdxFromParams) {
      swiperRef.current.slideTo(activeIdxFromParams, 0)
    }
  }, [activeIdxFromParams])

  return (
    <Row gap={15} class={styles.root}>
      <Col gap={15} justifyContent='center'>
        {TABS.map((tab, idx) => (
          <UI.Tab
            key={tab.id}
            active={activeIdxFromParams === idx}
            onClick={() => {
              setTabParam(idx)
              swiperRef.current?.slideTo(idx)
            }}
          >
            {tab.title}
          </UI.Tab>
        ))}
      </Col>

      <UI.Panel class={styles.panel}>
        <div class={styles.swiperWrapper}>
          <Swiper
            initialSlide={activeIdxFromParams}
            slidesPerView={1}
            className={styles.swiper}
            onSwiper={(swiper) => {
              swiperRef.current = swiper

              if (swiper.activeIndex !== activeIdxFromParams) {
                swiper.slideTo(activeIdxFromParams, 0)
              }
            }}
            onSlideChange={(swiper) => {
              setTabParam(swiper.activeIndex)
            }}
          >
            {TABS.map(tab => (
              <SwiperSlide key={tab.id} class={styles.swiperSlide}>
                <div class={styles.list}>
                  {tab.questions(props.data.persone2Gender).map(question => (
                    <button
                      key={question}
                      class={styles.item}
                      onClick={() => {
                        askQuestion(question)
                      }}
                    >
                      <Text
                        fontSize={16}
                        fontSizeMobile={15}
                      >
                        {question}
                      </Text>

                      <ChevronRightSvg
                        width={20}
                        height={20}
                        strokeWidth={1.5}
                        class={styles.chevron}
                      />
                    </button>
                  ))}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </UI.Panel>

      <UI.Button
        size='l'
        type='primary'
        onClick={() => {
          metrikaGoal('synastry_question_click')

          const q = encodeURIComponent(
            `Хочу задать свой вопрос про ${props.data.persone2Accusative}. Учитывай нашу совместимость и ${
              props.data.persone2Gender === 'female' ? 'её' : 'его'
            } дату рождения: ${dayjs(props.data.persone2Date, 'YYYY-MM-DD').format('DD.MM.YYYY')}.`,
          )

          route(`~/chats/${props.data.type === 'astro' ? 'zoe' : 'milana'}?q=${q}&template=synastry`)
        }}
      >
        Задать свой вопрос
      </UI.Button>
    </Row>
  )
}
